import { find, get, has, isNil } from 'lodash';
import config from 'config';

import { getName } from 'modules/appointmentInfo/utils/participantInfo';
import { appointmentHavePermission } from 'modules/common/selectors/permissions';
import * as appointmentStatuses from 'modules/common/constants/appointmentStatuses';
import { CUSTOMER, PROVIDER } from 'modules/common/constants/conversationParticipantsTypes';
import { inConversationStatuses, PENDING } from 'modules/common/constants/conversationStatuses';
import {
  APPOINTMENTS_CUSTOMER_AVAILABILITY_STATUS,
  APPOINTMENTS_PROVIDER_AVAILABILITY_STATUS,
} from 'modules/common/constants/appointmentPermissionResources';
import { selfAvailability } from '../selectors/selfAvailability';
import { FACE_TO_FACE } from '../../constants/conversationTypes';


export const mapAppointment = appointment => {
  const provider = find(appointment.participants, { participantType: PROVIDER });
  const providerGroupSpecialty = appointment.providerGroupSpecialty;
  const assignedToGroup = isNil(provider) && !providerGroupSpecialty;
  const customer = find(appointment.participants, { participantType: CUSTOMER });
  const { filesPrerequisitesConfig } = appointment.service;

  const subjectProfile = get(appointment, 'subjects[0]');

  const isF2F = appointment.allowedConversationTypes[0] === FACE_TO_FACE;
  const subject = {
    ...subjectProfile.user.profile,
    id: subjectProfile.user.userId,
    subjectName: getName(subjectProfile.user.profile, { withMiddleName: true }),
    description: subjectProfile.description,
  };

  const isClosed = appointmentStatuses.CLOSED_APPOINTMENT_STATUSES.includes(appointment.status);
  const closeTime = isClosed ? get(appointment, 'completedTimestamp', null) : null;
  const schedules = appointment.schedules.filter(({ status }) => status === 'CREATED');

  const overdue = !appointment.conversations.some((conversation) => inConversationStatuses.includes(conversation.status))
    && has(schedules, '[0].startTime')
    && (schedules[0].startTime - Date.now()) <= config.overdue_time;

  const queueType = get(appointment, 'service.config.queueType');

  const canSeeProviderAvailability =
    appointmentHavePermission(appointment, { [APPOINTMENTS_PROVIDER_AVAILABILITY_STATUS]: ['read'] })
    && appointment.service.config.providerAvailabilityEnabled;
  const canSeeCustomerAvailability =
    appointmentHavePermission(appointment, { [APPOINTMENTS_CUSTOMER_AVAILABILITY_STATUS]: ['read'] })
    && appointment.service.config.customerAvailabilityEnabled;
  const providerAvailability = get(provider, 'userAvailability.status');
  const customerAvailability = get(customer, 'userAvailability.status');
  const isPatientInWaitingRoom = appointment.conversations.some(conversation => conversation.status === PENDING);
  const groupInfo = {
    isGroup: !!appointment.group,
    index: 0,
    total: 0,
  };

  if (groupInfo.isGroup) {
    const tempApptGroup = appointment.group.appointments.filter(
      tempAppt => isClosed
        ? appointmentStatuses.CLOSED_APPOINTMENT_STATUSES.includes(tempAppt.status)
        : !appointmentStatuses.CLOSED_APPOINTMENT_STATUSES.includes(tempAppt.status),
    );

    groupInfo.total = tempApptGroup.length;
    groupInfo.index = tempApptGroup.findIndex(tempAppt => tempAppt.appointmentId === appointment.appointmentId) + 1;
  }

  return {
    ...appointment,
    serviceName: isNil(appointment.service) ? null : appointment.service.name,
    provider: (assignedToGroup || providerGroupSpecialty) ? null : {
      ...provider.user.profile,
      id: provider.user.userId,
      type: provider.participantType,
      availability: canSeeProviderAvailability && !isClosed && !!providerAvailability && (selfAvailability(provider.user.userId, providerAvailability)),
      participantName: getName(provider.user.profile, { withMiddleName: true }),
    },
    assignedToGroup,
    providerGroupSpecialty,
    customer: {
      ...customer.user.profile,
      id: customer.user.userId,
      type: customer.participantType,
      availability: canSeeCustomerAvailability && !isClosed && !!customerAvailability && (selfAvailability(customer.user.userId, customerAvailability)),
      participantName: getName(customer.user.profile, { withMiddleName: true }),
      isPatientInWaitingRoom,
    },
    subject,
    schedules: schedules.map((schedule) => ({
      ...schedule,
      startTime: String(schedule.startTime),
      endTime: String(schedule.endTime),
    })),
    closeTime: closeTime ? String(closeTime) : null,
    overdue,
    queueType,
    filesPrerequisitesConfig,
    groupInfo,
    caseConfig: appointment.case,
    isF2F,
    faceToFaceType: appointment.faceToFaceType,
  };
};
